interface BrandColorScheme {
  lowlight: string;
  darker: string;
  dark: string;
  main: string;
  light: string;
  lighter: string;
  highlight: string;
  contrastText: string;
  buttonHover: string;
}

interface CommonColorScheme {
  dark: string;
  regular: string;
  light: string;
  highlight: string;
  darkerHighlight: string;
  highlightLight: string;
  contrastText: string;
}

export const neutralColor: Record<number, string> = {
  0: '#FFFFFF',
  50: '#FAFBFF',
  75: '#F9FAFC',
  100: '#F4F6FA',
  200: '#EDEFF5',
  300: '#E6E8F0',
  400: '#D8DAE5',
  500: '#C1C4D6',
  600: '#8F95B2',
  700: '#696F8C',
  800: '#474D66',
  900: '#252836',
  950: '#1A1D26',
  1000: '#000000',
} as const;

// Change these to your company's brand colors.
//     1. `main` is the only color necessary, but all the
//   other ones are recomended.
//     2. `highlight` is advised be way lighter the the
//   `lighter` counterpart.
//     3. `lowlight` is advised be way darker the the
//   `darker` counterpart.
//     4. Its nice if you can match colors from one group
//   as contrast color for another, but its not always
//   possible given the palette.
export const brandColors: {
  primary: BrandColorScheme;
  secondary: BrandColorScheme;
  terciary: BrandColorScheme;
} = {
  // Talentum purple
  // cores alteradas: darker, dark, main, light, lighter, buttonHover
  primary: {
    lowlight: '#0f0227',
    darker: '#001354',
    dark: '#002486',
    main: '#093FCF',
    light: '#264FDD',
    lighter: '#466AF7',
    highlight: '#f2ebfe', // also contrastText for primary
    contrastText: '#f2ebfe', // same as primary highlight
    buttonHover: '#002486', // same as dark
  },
  // Talentum orange
  secondary: {
    lowlight: '#351909',
    darker: '#8c4417',
    dark: '#af551d',
    main: '#ce6321',
    light: '#dd702c',
    lighter: '#e07c3e',
    highlight: '#f9e7dc',
    contrastText: '#fcf0d9', // same as terciary highlight
    buttonHover: '#af551d', // same as dark
  },
  // Talentum ochre
  terciary: {
    lowlight: '#382605', // also contrastText for terciary
    darker: '#bc8010',
    dark: '#e19914',
    main: '#eca624',
    light: '#efb343',
    lighter: '#f0ba56',
    highlight: '#fcf0d9', // also contrastText for secondary
    contrastText: '#382605', // same as terciary lowlight
    buttonHover: '#e19914', // same as dark
  },
};

export const commonColors: Record<string, CommonColorScheme> = {
  blue: {
    dark: '#0F3699',
    regular: '#083FCF',
    light: '#2B62F3',
    highlight: '#E4ECFF',
    darkerHighlight: '#CDDBFF',
    highlightLight: '#F3F6FF',
    contrastText: '#E4ECFF',
  },
  green: {
    dark: '#088A59',
    regular: '#17AE75',
    light: '#41D19B',
    highlight: '#EEF8F4',
    darkerHighlight: '#DCF2EA',
    highlightLight: '#F5FBF8',
    contrastText: '#EEF8F4',
  },
  red: {
    dark: '#A70F0F',
    regular: '#DA2424',
    light: '#F05252',
    highlight: '#F9DADA',
    darkerHighlight: '#F4B6B6',
    highlightLight: '#FDF4F4',
    contrastText: '#F9DADA',
  },
  yellow: {
    dark: '#ED9419',
    regular: '#F6B941',
    light: '#F8C74E',
    highlight: '#FFEFD2',
    darkerHighlight: '#FFDFA6',
    highlightLight: '#FFFAF1',
    contrastText: '#FFEFD2',
  },
  orange: {
    dark: '#EC6226',
    regular: '#F67741',
    light: '#F78758',
    highlight: '#F9E5DA',
    darkerHighlight: '#F2D5C4',
    highlightLight: '#FDF1EA',
    contrastText: '#F9E5DA',
  },
  purple: {
    dark: '#5B02B4',
    regular: '#6C08CF',
    light: '#801DE3',
    highlight: '#F1E4FF',
    darkerHighlight: '#E3CCFC',
    highlightLight: '#F8F2FF',
    contrastText: '#F1E4FF',
  },
};

export const progressColors: Record<number, string> = {
  0: '#f62e44',
  126: '#f65046',
  251: '#f77649',
  376: '#fb8d33',
  501: '#ffa41c',
  626: '#c4b11f',
  751: '#84bf23',
  876: '#4eae37',
  1000: '#169c4c',
} as const;

export const colorTokens = {
  border: {
    default: neutralColor[400],
    light: neutralColor[200],
  },
  white: neutralColor[0],
  black: neutralColor[1000],
  icon: neutralColor[800],
  brandPrimary: brandColors.primary.main,
  brandPrimaryHighlight: neutralColor[200],
  body: {
    background: neutralColor[100],
    containerDivLight: neutralColor[200],
    containerDivDark: neutralColor[300],
    placeholder: neutralColor[400],
  },
  button: {
    disabled: {
      background: neutralColor[400],
      text: neutralColor[700],
    },
  },
  textInput: {
    background: neutralColor[0],
    border: neutralColor[400],
    textFilled: neutralColor[1000],
    textPlaceholder: neutralColor[600],
  },
  sidePanel: {
    background: neutralColor[200],
  },
  text: {
    primary: neutralColor[0],
    secondary: neutralColor[0],
  },
};
